import { ButtonProps, Image, ImageViewerBehavior, OverlayProps, Panel, PanelProps } from "@mcleod/components";
import { PinchZoomHandler } from "./PinchZoomHandler";
import { DeviceAccessor } from "@mcleod/core";

const black = "#000";
const white = "#FFF"

/**
 * Tracks active PinchZoomHandler instances by image element.
 * Used to ensure we don't attach multiple handlers to the same image.
 */
const pinchZoomHandlers = new WeakMap<HTMLElement, PinchZoomHandler>();

export class AppImageViewerBehavior extends ImageViewerBehavior {
    private pinchZoomHandler: { detach(): void } | undefined;

    override get addlHeaderProps(): Partial<PanelProps> {
        return { backgroundColor: black };
    }

    override get defaultOverlayProps(): Partial<OverlayProps> {
        return {
            style: {
                width: "94%",
                height: "94%",
                position: "absolute",
                left: "3%",
                top: "3%",
                backgroundColor: black,
                borderRadius: "4px"
            },
            greyedBackground: true,
            closeOnClickOff: true,
            coverPageHeader: true,
        }
    }

    get addlCloseButtonProps(): Partial<ButtonProps> {
        return { imageWidth: 24, imageHeight: 24, rowBreak: false, imageColor: white, padding: 8 };
    }

    get closeButtonLocation(): 'left' | 'right' {
        return 'left';
    }

    override get allowEmailDefault(): boolean {
        return false;
    }

    override get allowDownloadsDefault(): boolean {
        return false;
    }

    override get showOpenInButtonsDefault(): boolean {
        return false;
    }

    override get displayZoomControlsDefault(): boolean {
        return false;
    }

    public override syncZoomControls(image: Image, controlsPanel: Panel) {
        controlsPanel.removeAll();
        controlsPanel.visible = false;

        this.syncPinchToZoom(image);
    }

    /**
     * Enables or disables pinch-to-zoom based on the behavior settings.
     * This is called when the image is loaded.
     */
    private syncPinchToZoom(image: Image): void {
        // Clean up existing handler if present
        if (this.pinchZoomHandler) {
            this.pinchZoomHandler.detach();
            this.pinchZoomHandler = undefined;
        }

        // Enable pinch-to-zoom if behavior supports it and image is present
        if (this.isPinchToZoomEnabled && image?.isEmpty() === false) {
            this.pinchZoomHandler = this.enablePinchToZoomOnImage(image);
        }
    }

    /**
     * Returns true if pinch-to-zoom should be enabled for images.
     * This is an app-only feature.
     */
    get isPinchToZoomEnabled(): boolean {
        return DeviceAccessor.isApp && !DeviceAccessor.isAppTestingViaWeb();
    }

    /**
     * Enables pinch-to-zoom on the given Image component.
     * Call this after the image is loaded/mounted.
     *
     * @param image The Image component to enable pinch-to-zoom on
     * @returns The PinchZoomHandler instance, or undefined if already attached
     */
    public enablePinchToZoomOnImage(image: Image): PinchZoomHandler | undefined {
        if (!image?._element) {
            return undefined;
        }

        if (pinchZoomHandlers.has(image._element)) {
            return pinchZoomHandlers.get(image._element);
        }

        const handler = new PinchZoomHandler(image);
        pinchZoomHandlers.set(image._element, handler);

        return handler;
    }

    /**
     * Disables pinch-to-zoom on the given Image component.
     *
     * @param image The Image component to disable pinch-to-zoom on
     */
    public disablePinchToZoomOnImage(image: Image): void {
        if (!image?._element) {
            return;
        }

        const handler = pinchZoomHandlers.get(image._element);
        if (handler) {
            handler.detach();
            pinchZoomHandlers.delete(image._element);
        }
    }
}

if (DeviceAccessor.isApp) {
    AppImageViewerBehavior.register();
}
